import { InjectionToken, Injector, OnDestroy, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subscription } from 'rxjs';
import { debounceTime, first, tap } from 'rxjs/operators';

import { DynamicEntityKey, StepData, ValidationError } from 'models';

import {
  enhanceFormGroupWithOptions, getFormValidationErrors,
  getFormValueWithDisabledFields,
  inferDisabledFormGroupControlsKeys,
  inferStepConstraintGroup,
  inferStepOptionsGroup,
  inferStepValueGroup,
  markFromGroupControlsAsTouchedAndDirty
} from 'helpers';
import { AnalyticsService, EnvironmentService, ExecService, FormsService, ScrollService } from 'services';
import { AppStoreFacadeService } from '../../../../facades/app-store-facade.service';
import * as FormConstants from '../../../shared/constants/form.constants';

export abstract class ProcessStepBaseComponent implements OnDestroy {
  public FormConstants = FormConstants;
  public formGroup: FormGroup;
  public shouldHaveBackButton = false;
  public shouldBeSticky = true;

  protected subscriptions: Subscription = new Subscription();
  protected alwaysDisabledFormControlsKeys: string[] = [];
  protected storeFormDataFunction: (formData: any) => void;

  protected appStoreFacadeService: AppStoreFacadeService;
  protected formsService: FormsService;
  protected scrollService: ScrollService;
  protected translateService: TranslateService;
  protected analyticsService: AnalyticsService;
  protected execService: ExecService;
  protected environmentService: EnvironmentService;

  protected constructor(protected injector: Injector) {
    this.injectServices();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public goToPreviousStep() {
    this.appStoreFacadeService.navigation.navigateToPrevious();
  }

  public goToNextStep() {
    if (!this.storeFormDataFunction) {
      console.warn('Missing storeFormDataFunction');
      return;
    }
    if (this.formGroup.invalid) {
      console.warn('Form is invalid');
      this.markFormAsDirtyAndScrollToError();

      try {
        const INPUT_PREFIX = 'wt-input-error-';
        const errors = getFormValidationErrors(this.formGroup.controls).map(r => {
          r.description = this.translateService.instant(`${INPUT_PREFIX}${r.description}`) ||
            this.translateService.instant(`${r.description}`);
          return r;
        });
        this.analyticsService.sendFormError(errors);
      } catch (ex) {
        console.warn(ex);
      }

      return;
    }

    // We should re-enable previously disabled form control (only those disabled by backend)
    // so that we are sure to pass the values.
    const formData: any = getFormValueWithDisabledFields(this.formGroup, this.alwaysDisabledFormControlsKeys || []);
    this.formGroup.markAsPristine();
    this.storeFormDataFunction(formData);
    this.appStoreFacadeService.navigation.goNext();
  }

  public canDeactivate(): boolean {
    // Disable modal to come back
    return true;
    /* OLD CODEif (!this.formGroup) {
      OLD CODE// No form, no block
      OLD CODEreturn true;
    }

    OLD CODE return !this.formGroup.dirty;*/
  }

  public shouldShowBackButton() {
    this.subscriptions.add(
      this.appStoreFacadeService.navigation.previousStepId$
        .subscribe(previousStep => {
          this.shouldHaveBackButton = previousStep ?  true : false;
          return this.shouldHaveBackButton;
        }
      )
    );
  }
  public isStickyNavigation() {
    this.subscriptions.add(
      this.scrollService.stickyStatus().subscribe(status => {
        // this.shouldBeSticky = status;
      })
    );
  }
  protected setFormStoreDataFunction(storeDataFunction) {
    this.storeFormDataFunction = storeDataFunction;
  }

  protected setFormGroup(formGroup: FormGroup) {
    this.formGroup = formGroup;
    (window as any).FORM_GROUP = formGroup;
    // Attach input validation errors listener
    this.subscriptions.add(
      this.appStoreFacadeService.errors.inputValidationErrors$
        .pipe(
          tap((inputValidationError: ValidationError) => {
            this.formsService.enhanceFormGroupWithValidationError(this.formGroup, inputValidationError);
          }),
          debounceTime(50),
        )
        .subscribe((inputValidationError: ValidationError) => {
          // debounceTime pipe will guarantee that function will be called once per round
          this.markFormAsDirtyAndScrollToError(inputValidationError);
        })
    );
  }

  protected setFormGroupStepData(stepData: StepData) {
    this.setFormGroupValue(inferStepValueGroup(stepData));
    this.setFormGroupConstraint(inferStepConstraintGroup(stepData));
    this.setFormGroupOptions(inferStepOptionsGroup(stepData));
  }

  protected setFormGroupValue(formData: any) {
    this.formGroup.patchValue(formData);
  }

  protected setFormGroupConstraint(formConstraint: any) {
    Object.keys(formConstraint || {}).forEach((stepDataConstraintKey: DynamicEntityKey) => {
      const formGroupConstraints = formConstraint[stepDataConstraintKey];
      const formGroup = this.formGroup.get(stepDataConstraintKey) as FormGroup;

      this.formsService.enhanceFormGroupWithConstraints(formGroup, formGroupConstraints);
    });
  }

  protected setFormGroupOptions(formOptions: any) {
    Object.keys(formOptions || {}).forEach((optionsKey: DynamicEntityKey) => {
      const formGroupOptions = formOptions[optionsKey];
      const formGroup = this.formGroup.get(optionsKey) as FormGroup;
      enhanceFormGroupWithOptions(formGroup, formGroupOptions);
    });

    // Infer disabled form controls keys
    this.alwaysDisabledFormControlsKeys = inferDisabledFormGroupControlsKeys(formOptions);
  }

  protected markFormAsDirtyAndScrollToError(inputValidationError?: ValidationError, form?: FormGroup) {
    markFromGroupControlsAsTouchedAndDirty(form || this.formGroup);
    this.checkCustomInvalidFields(inputValidationError);
    this.scrollService.scrollToFirstInvalidInput();
  }

  public checkCustomInvalidFields(inputValidationError?: ValidationError) {
    return null;
  }

  public injectServiceInstance = (token: Type<any> | InjectionToken<any>) => {
    const injectedInstance = this.injector.get(token, null);

    if (!injectedInstance) {
      console.warn(`Dependency injection problem, missing: ${ token }`);
    }

    return injectedInstance;
  }

  protected sendAnalyticsData(forceStepId?: string) {
    combineLatest([
      this.appStoreFacadeService.navigation.currentStepId$,
      this.appStoreFacadeService.store$
    ])
      .pipe(first(([ stepId, store ]) => Boolean(store)))
      .subscribe(([ stepId, store ]) => {
        this.analyticsService.generateAndSendStepPayload(stepId, store, forceStepId);
      });
  }

  private injectServices() {
    this.appStoreFacadeService = this.injectServiceInstance(AppStoreFacadeService as Type<AppStoreFacadeService>);
    this.formsService = this.injectServiceInstance(FormsService as Type<FormsService>);
    this.scrollService = this.injectServiceInstance(ScrollService as Type<ScrollService>);
    this.translateService = this.injectServiceInstance(TranslateService as Type<TranslateService>);
    this.analyticsService = this.injectServiceInstance(AnalyticsService as Type<AnalyticsService>);
    this.execService = this.injectServiceInstance(ExecService as Type<ExecService>);
    this.environmentService = this.injectServiceInstance(EnvironmentService as Type<EnvironmentService>);
  }
}
